// src/pages/Profile.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getProfile, updateProfile } from "../redux/slices/authSlice";
import { getKycStatus, submitKyc } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  User,
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Upload,
  Camera,
  FileText,
  Car,
  CreditCard,
  AlertCircle,
  Save,
  X,
  Edit,
  Sparkles,
  Key,
  Wallet,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat } from "../utils/currency";

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const { user, isLoading } = useSelector((state: RootState) => state.auth);
  const { kyc, isLoading: kycLoading } = useSelector(
    (state: RootState) => state.kyc,
  );
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
  });
  const [showKycForm, setShowKycForm] = useState(false);
  const [documentType, setDocumentType] = useState<
    "passport" | "id_card" | "driver_license"
  >("passport");
  const [documentFront, setDocumentFront] = useState<File | null>(null);
  const [documentBack, setDocumentBack] = useState<File | null>(null);
  const [selfiePhoto, setSelfiePhoto] = useState<File | null>(null);
  const [previewFront, setPreviewFront] = useState<string>("");
  const [previewBack, setPreviewBack] = useState<string>("");
  const [previewSelfie, setPreviewSelfie] = useState<string>("");

  useEffect(() => {
    dispatch(getProfile() as any);
    dispatch(getKycStatus() as any);
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
      });
    }
  }, [user]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back" | "selfie",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "front") {
        setDocumentFront(file);
        setPreviewFront(reader.result as string);
      } else if (type === "back") {
        setDocumentBack(file);
        setPreviewBack(reader.result as string);
      } else {
        setSelfiePhoto(file);
        setPreviewSelfie(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    try {
      const updateData: any = {};
      if (formData.first_name.trim())
        updateData.first_name = formData.first_name.trim();
      if (formData.last_name.trim())
        updateData.last_name = formData.last_name.trim();
      if (formData.username.trim())
        updateData.username = formData.username.trim();

      if (Object.keys(updateData).length === 0) {
        toast.error("No changes made");
        return;
      }

      await dispatch(updateProfile(updateData) as any);

      toast.success("Profile updated successfully");
      setEditMode(false);
      dispatch(getProfile() as any);
    } catch (error: any) {
      toast.error(error.message || "Failed to update profile");
    }
  };

  const handleKycSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!documentFront || !selfiePhoto) {
      toast.error("Please upload all required documents");
      return;
    }

    if (documentType !== "passport" && !documentBack) {
      toast.error("Please upload both sides of your document");
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("document_front", documentFront);
    if (documentBack) formData.append("document_back", documentBack);
    formData.append("selfie_photo", selfiePhoto);

    try {
      await dispatch(submitKyc(formData) as any);
      toast.success("KYC submitted successfully!");
      setShowKycForm(false);
      resetKycForm();
    } catch (error) {
      toast.error("Failed to submit KYC");
    }
  };

  const resetKycForm = () => {
    setDocumentFront(null);
    setDocumentBack(null);
    setSelfiePhoto(null);
    setPreviewFront("");
    setPreviewBack("");
    setPreviewSelfie("");
    setDocumentType("passport");
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case "passport":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "id_card":
        return <CreditCard className="h-5 w-5 text-purple-500" />;
      case "driver_license":
        return <Car className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading profile...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  My Profile
                </h1>
                <Sparkles className="h-4 w-4 text-primary/60" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Manage your account information and verification
              </p>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormData({
                        first_name: user.first_name || "",
                        last_name: user.last_name || "",
                        username: user.username || "",
                      });
                    }}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group/cancel`}
                  >
                    <X className="h-4 w-4 mr-2 transition-transform group-hover/cancel:-translate-x-0.5" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group/save disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2 transition-transform group-hover/save:scale-110" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group/edit`}
                >
                  <Edit className="h-4 w-4 mr-2 transition-transform group-hover/edit:scale-110" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className={`${GlobalStyles.card} backdrop-blur-xl`}>
              <div className="flex items-center gap-2 mb-6">
                <User className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  Personal Information
                </h3>
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="group">
                    <label
                      className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                    >
                      <span>First Name</span>
                      <Sparkles className="h-3 w-3 text-primary/60" />
                    </label>
                    {editMode ? (
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              first_name: e.target.value,
                            })
                          }
                          className={`${GlobalStyles.input} pl-10`}
                          placeholder="Enter first name"
                        />
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      </div>
                    ) : (
                      <div className="flex items-center p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                        <User className="h-4 w-4 text-primary mr-3" />
                        <span className="text-gray-900 dark:text-white">
                          {user.first_name || "Not set"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="group">
                    <label className={GlobalStyles.inputLabel}>Last Name</label>
                    {editMode ? (
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              last_name: e.target.value,
                            })
                          }
                          className={`${GlobalStyles.input} pl-10`}
                          placeholder="Enter last name"
                        />
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      </div>
                    ) : (
                      <div className="flex items-center p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                        <User className="h-4 w-4 text-primary mr-3" />
                        <span className="text-gray-900 dark:text-white">
                          {user.last_name || "Not set"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="group">
                  <label
                    className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                  >
                    <span>Email</span>
                    <Mail className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="flex items-center p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                    <Mail className="h-4 w-4 text-primary mr-3" />
                    <span className="text-gray-900 dark:text-white flex-1">
                      {user.email}
                    </span>
                    {user.email_verified_at ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>

                <div className="group">
                  <label
                    className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                  >
                    <span>Username</span>
                    <Key className="h-3 w-3 text-primary/60" />
                  </label>
                  {editMode ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        className={`${GlobalStyles.input} pl-10`}
                        placeholder="Enter username"
                      />
                      <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                  ) : (
                    <div className="flex items-center p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                      <Key className="h-4 w-4 text-primary mr-3" />
                      <span className="text-gray-900 dark:text-white">
                        {user.username || "Not set"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="group">
                  <label
                    className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                  >
                    <span>Member Since</span>
                    <Calendar className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="flex items-center p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                    <Calendar className="h-4 w-4 text-primary mr-3" />
                    <span className="text-gray-900 dark:text-white">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            },
                          )
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* KYC Verification */}
            {!kyc && user.verification_level === 1 && (
              <div className={`${GlobalStyles.card} backdrop-blur-xl`}>
                <div className="text-center mb-6">
                  <div className="relative mx-auto w-20 h-20 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur-lg"></div>
                    <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                      <Shield className="h-10 w-10 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Complete KYC Verification
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    Verify your identity to unlock all platform features and
                    higher limits
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/30 dark:to-gray-900/30 rounded-xl border border-gray-200/30 dark:border-gray-700/30 text-center group/benefit">
                    <div className="relative mx-auto w-12 h-12 mb-3">
                      <div className="absolute inset-0 bg-green-500/10 rounded-full blur"></div>
                      <div className="relative w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center group-hover/benefit:scale-110 transition-transform">
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      </div>
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white mb-1">
                      Higher Limits
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Increased transaction limits
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/30 dark:to-gray-900/30 rounded-xl border border-gray-200/30 dark:border-gray-700/30 text-center group/benefit">
                    <div className="relative mx-auto w-12 h-12 mb-3">
                      <div className="absolute inset-0 bg-blue-500/10 rounded-full blur"></div>
                      <div className="relative w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center group-hover/benefit:scale-110 transition-transform">
                        <Shield className="h-6 w-6 text-blue-500" />
                      </div>
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white mb-1">
                      Enhanced Security
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Added account protection
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/30 dark:to-gray-900/30 rounded-xl border border-gray-200/30 dark:border-gray-700/30 text-center group/benefit">
                    <div className="relative mx-auto w-12 h-12 mb-3">
                      <div className="absolute inset-0 bg-purple-500/10 rounded-full blur"></div>
                      <div className="relative w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center group-hover/benefit:scale-110 transition-transform">
                        <Wallet className="h-6 w-6 text-purple-500" />
                      </div>
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white mb-1">
                      Full Access
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      All platform features
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowKycForm(true)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full group/kyc`}
                >
                  <Shield className="h-5 w-5 mr-2 transition-transform group-hover/kyc:scale-110" />
                  <span>Start KYC Verification</span>
                  <Sparkles className="h-4 w-4 ml-2 opacity-0 group-hover/kyc:opacity-100 transition-opacity" />
                </button>
              </div>
            )}
          </div>

          {/* Right Column - Verification Status */}
          <div className="space-y-6">
            {/* Verification Status */}
            <div className={`${GlobalStyles.card} backdrop-blur-xl`}>
              <div className="flex items-center gap-2 mb-6">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  Verification Status
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Verification
                  </span>
                  {user.email_verified_at ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    KYC Level 1
                  </span>
                  {user.verification_level >= 1 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    KYC Level 2
                  </span>
                  {user.verification_level >= 2 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    KYC Level 3
                  </span>
                  {user.verification_level >= 3 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center p-3 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                  <Shield className="h-5 w-5 text-primary mr-3" />
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">
                      Level {user.verification_level}/3
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Complete verification to unlock all features
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Statistics */}
            <div className={`${GlobalStyles.card} backdrop-blur-xl`}>
              <div className="flex items-center gap-2 mb-6">
                <Wallet className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  Account Statistics
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Account Balance
                  </span>
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    {formatFiat(user.balance)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Verification Level
                  </span>
                  <span className="font-bold text-primary">
                    {user.verification_level}/3
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Wallet Status
                  </span>
                  <span
                    className={`font-bold ${
                      user.wallet_connected
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {user.wallet_connected ? "Connected" : "Not Connected"}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/30 dark:border-gray-700/30">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Account Age
                  </span>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {user.created_at
                      ? Math.floor(
                          (new Date().getTime() -
                            new Date(user.created_at).getTime()) /
                            (1000 * 60 * 60 * 24),
                        ) + " days"
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KYC Form Modal */}
        {showKycForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-xl z-50 flex items-center justify-center p-5">
            <div className="relative w-full max-w-2xl">
              {/* Background Glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

              {/* Modal Content */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
                  <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                    KYC Verification
                  </h3>
                  <button
                    onClick={() => {
                      setShowKycForm(false);
                      resetKycForm();
                    }}
                    className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleKycSubmit} className="p-6 space-y-6">
                  {/* Document Type */}
                  <div>
                    <label
                      className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                    >
                      <span>Select Document Type</span>
                      <Sparkles className="h-3 w-3 text-primary/60" />
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {["passport", "id_card", "driver_license"].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setDocumentType(type as any)}
                          className={`p-4 rounded-xl border flex flex-col items-center justify-center transition-all duration-300 group/type ${
                            documentType === type
                              ? "border-primary bg-gradient-to-r from-primary/10 to-primary/5 scale-105"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:scale-102"
                          }`}
                        >
                          {getDocumentTypeIcon(type)}
                          <span className="mt-2 font-medium capitalize text-gray-900 dark:text-white">
                            {type.replace("_", " ")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Document Front */}
                  <div className="group">
                    <label
                      className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                    >
                      <span>Document Front</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 hover:border-primary transition-colors bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/50">
                      {previewFront ? (
                        <div className="relative">
                          <img
                            src={previewFront}
                            alt="Document Front"
                            className="w-full h-auto rounded-lg max-h-64 object-contain mx-auto"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setDocumentFront(null);
                              setPreviewFront("");
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer">
                          <div className="relative mb-4">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur"></div>
                            <div className="relative w-16 h-16 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                              <Upload className="h-8 w-8 text-gray-400" />
                            </div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                            Upload clear photo of document front
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            JPG, PNG (Max 5MB)
                          </p>
                          <div
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group/upload`}
                          >
                            Select File
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileChange(e, "front")}
                              required
                            />
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Document Back (if not passport) */}
                  {documentType !== "passport" && (
                    <div className="group">
                      <label className={GlobalStyles.inputLabel}>
                        Document Back
                      </label>
                      <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 hover:border-primary transition-colors bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/50">
                        {previewBack ? (
                          <div className="relative">
                            <img
                              src={previewBack}
                              alt="Document Back"
                              className="w-full h-auto rounded-lg max-h-64 object-contain mx-auto"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setDocumentBack(null);
                                setPreviewBack("");
                              }}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center cursor-pointer">
                            <div className="relative mb-4">
                              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur"></div>
                              <div className="relative w-16 h-16 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                                <Upload className="h-8 w-8 text-gray-400" />
                              </div>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                              Upload clear photo of document back
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                              JPG, PNG (Max 5MB)
                            </p>
                            <div
                              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                            >
                              Select File
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFileChange(e, "back")}
                              />
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Selfie Photo */}
                  <div className="group">
                    <label
                      className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
                    >
                      <span>Selfie with Document</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 hover:border-primary transition-colors bg-gradient-to-br from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/50">
                      {previewSelfie ? (
                        <div className="relative">
                          <img
                            src={previewSelfie}
                            alt="Selfie"
                            className="w-full h-auto rounded-lg max-h-64 object-contain mx-auto"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setSelfiePhoto(null);
                              setPreviewSelfie("");
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center cursor-pointer">
                          <div className="relative mb-4">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/10 rounded-full blur"></div>
                            <div className="relative w-16 h-16 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                              <Camera className="h-8 w-8 text-gray-400" />
                            </div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 mb-2 text-center">
                            Take a selfie while holding your document
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Face and document must be clearly visible
                          </p>
                          <div
                            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
                          >
                            Take Photo
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileChange(e, "selfie")}
                              capture="environment"
                              required
                            />
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div
                    className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} backdrop-blur-sm`}
                  >
                    <AlertCircle className="h-5 w-5 flex-shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Verification Requirements:
                      </p>
                      <ul className="text-sm mt-2 space-y-1 text-gray-600 dark:text-gray-400">
                        <li className="flex items-start">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>
                            All documents must be valid and not expired
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>
                            Photos must be clear and all text readable
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>
                            Selfie must clearly show your face and the document
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>
                            All information must match your account details
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                    <button
                      type="button"
                      onClick={() => {
                        setShowKycForm(false);
                        resetKycForm();
                      }}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonMedium} group/cancel`}
                    >
                      <span>Cancel</span>
                      <X className="h-4 w-4 ml-1 opacity-0 group-hover/cancel:opacity-100 transition-opacity" />
                    </button>
                    <button
                      type="submit"
                      disabled={kycLoading}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group/submit disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {kycLoading ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></div>
                          Submitting...
                        </div>
                      ) : (
                        <>
                          <Shield className="h-4 w-4 mr-2 transition-transform group-hover/submit:scale-110" />
                          <span>Submit for Verification</span>
                          <Sparkles className="h-4 w-4 ml-2 opacity-0 group-hover/submit:opacity-100 transition-opacity" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
